import { useState, useCallback, useRef, useEffect } from 'react'
import type { NotebookData, NotebookNote } from '../../shared/types/notebook'
import { ApiError } from '../../shared/api/base'
import { getNotebookEntries, createNotebookEntry, updateNotebookEntry, deleteNotebookEntry, batchUpdateSortOrder } from './api'
import { reindexSiblings, needsReindex } from './dndTree'
import { findNote } from './notebookTree'
import { getPendingRecordings, type PendingRecording } from '../recording'

export interface UseNotebookStateResult {
  data: NotebookData | null
  setData: React.Dispatch<React.SetStateAction<NotebookData | null>>
  loading: boolean
  currentPageId: string | null
  navStack: string[]
  newPageId: string | null
  autoExpandIds: string[]
  /** Returns a stable key for AnimatePresence that doesn't change during temp→server ID swaps */
  getStableKey: (id: string) => string
  navigateToPage: (id: string) => void
  navigateBack: () => void
  handleTogglePin: (noteId: string) => void
  handleDeleteNote: (noteId: string) => void
  handleUpdateNote: (noteId: string, updates: Partial<NotebookNote>) => void
  handleCreateChild: (parentId: string | null) => void
  handleMoveNote: (noteId: string, newParentId: string | null, newSortOrder: number) => void
  handleNewPage: () => void
  loadData: () => Promise<void>
  /** Resolves once any in-flight or queued PATCH for this note has settled. */
  flushSaves: (noteId: string) => Promise<void>
}

const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches

export function useNotebookState(): UseNotebookStateResult {
  const [data, setData] = useState<NotebookData | null>(null)
  const [loading, setLoading] = useState(true)

  // Navigation state
  const [currentPageId, setCurrentPageId] = useState<string | null>(null)
  const [navStack, setNavStack] = useState<string[]>([])
  const [newPageId, setNewPageId] = useState<string | null>(null)
  // IDs that should be auto-expanded in the sidebar tree (e.g. after creating a child page)
  const [autoExpandIds, setAutoExpandIds] = useState<string[]>([])

  const pendingPollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Stable key map: serverId → tempId, so AnimatePresence doesn't remount
  // the page view when the temp ID is swapped to the server-assigned ID.
  const stableKeyMap = useRef(new Map<string, string>())
  const getStableKey = useCallback((id: string) => {
    return stableKeyMap.current.get(id) ?? id
  }, [])

  const mergePendingRecordings = useCallback(async (notes: NotebookNote[]) => {
    try {
      const pending = await getPendingRecordings()
      if (pending.length === 0) {
        // Stop polling if no more pending
        if (pendingPollRef.current) {
          clearInterval(pendingPollRef.current)
          pendingPollRef.current = null
        }
        return notes
      }

      // Find the Meetings root page
      const meetingsRoot = notes.find(n => n.tags.includes('_meetings_root'))
      const meetingsParentId = meetingsRoot?.id ?? null

      // Filter out recordings that already have a notebook entry
      const existingEntryIds = new Set(notes.map(n => n.id))
      const newPending = pending.filter(
        r => !existingEntryIds.has(`pending-recording-${r.id}`)
      )

      const syntheticNotes: NotebookNote[] = newPending.map(r => ({
        id: `pending-recording-${r.id}`,
        parentId: meetingsParentId,
        sortOrder: -1,
        scope: 'core' as const,
        entryDate: null,
        title: r.status === 'transcribing' || r.status === 'uploaded' ? 'Transcribing...' : 'Generating notes...',
        icon: '🎙️',
        contentMd: '',
        summary: '',
        confidence: null,
        tags: ['recording', '_pending'],
        pinned: false,
        createdAt: r.createdAt,
        updatedAt: r.createdAt,
      }))

      // Remove any existing synthetic pending notes first, then add fresh ones
      const withoutOldPending = notes.filter(n => !n.tags.includes('_pending'))
      return [...syntheticNotes, ...withoutOldPending]
    } catch {
      return notes
    }
  }, [])

  // Tracks whether we've completed the first load. Subsequent calls (e.g. the
  // refresh after regenerating a meeting summary) skip the skeleton so the
  // open page doesn't unmount mid-flow and lose local state like the Notes
  // tab editor.
  const hasLoadedRef = useRef(false)
  const loadData = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true)
    try {
      const result = await getNotebookEntries()
      const entries = result.entries
      const merged = await mergePendingRecordings(entries)
      const availableTags = [...new Set(merged.flatMap(e => e.tags || []))]
      setData({ notes: merged, availableTags })

      // Start polling if there are pending recordings
      const hasPending = merged.some(n => n.tags.includes('_pending'))
      if (hasPending && !pendingPollRef.current) {
        pendingPollRef.current = setInterval(async () => {
          try {
            const pending = await getPendingRecordings()
            if (pending.length === 0) {
              // All done — reload full data and stop polling
              if (pendingPollRef.current) {
                clearInterval(pendingPollRef.current)
                pendingPollRef.current = null
              }
              const freshResult = await getNotebookEntries()
              const freshEntries = freshResult.entries
              const freshTags = [...new Set(freshEntries.flatMap(e => e.tags || []))]
              setData({ notes: freshEntries, availableTags: freshTags })
            } else {
              // Update synthetic notes with latest status
              setData(prev => {
                if (!prev) return prev
                const withoutPending = prev.notes.filter(n => !n.tags.includes('_pending'))
                const meetingsRoot = withoutPending.find(n => n.tags.includes('_meetings_root'))
                const meetingsParentId = meetingsRoot?.id ?? null

                const syntheticNotes: NotebookNote[] = pending.map((r: PendingRecording) => ({
                  id: `pending-recording-${r.id}`,
                  parentId: meetingsParentId,
                  sortOrder: -1,
                  scope: 'core' as const,
                  entryDate: null,
                  title: r.status === 'transcribing' || r.status === 'uploaded' ? 'Transcribing...' : 'Generating notes...',
                  icon: '🎙️',
                  contentMd: '',
                  summary: '',
                  confidence: null,
                  tags: ['recording', '_pending'],
                  pinned: false,
                  createdAt: r.createdAt,
                  updatedAt: r.createdAt,
                }))

                return {
                  ...prev,
                  notes: [...syntheticNotes, ...withoutPending],
                }
              })
            }
          } catch {
            // Ignore polling errors
          }
        }, 5000)
      }
    } catch (err) {
      console.error('Failed to load notebook:', err)
    } finally {
      setLoading(false)
      hasLoadedRef.current = true
    }
  }, [mergePendingRecordings])

  const navigateToPage = useCallback((id: string) => {
    if (!isDesktop()) {
      setNavStack(prev => currentPageId ? [...prev, currentPageId] : prev)
    }
    setCurrentPageId(id)
  }, [currentPageId])

  const navigateBack = useCallback(() => {
    setNavStack(prev => {
      const next = [...prev]
      const parentId = next.pop() ?? null
      setCurrentPageId(parentId)
      return next
    })
  }, [])

  const handleTogglePin = useCallback(async (noteId: string) => {
    if (!data) return
    const note = findNote(data.notes, noteId)
    if (!note) return
    const newPinned = !note.pinned
    setData(prev => {
      if (!prev) return prev
      return {
        ...prev,
        notes: prev.notes.map(n =>
          n.id === noteId ? { ...n, pinned: newPinned } : n
        ),
      }
    })
    updateNotebookEntry(noteId, { pinned: newPinned }).catch(err => {
      console.error('Failed to toggle pin:', err)
      setData(prev => {
        if (!prev) return prev
        return {
          ...prev,
          notes: prev.notes.map(n =>
            n.id === noteId ? { ...n, pinned: !newPinned } : n
          ),
        }
      })
    })
  }, [data])

  const handleDeleteNote = useCallback(async (noteId: string) => {
    if (!data) return
    if (currentPageId === noteId) {
      if (isDesktop()) {
        setCurrentPageId(null)
      } else {
        navigateBack()
      }
    }
    const removedNotes = data.notes.filter(n => n.id === noteId)
    setData(prev => {
      if (!prev) return prev
      return {
        ...prev,
        notes: prev.notes.filter(n => n.id !== noteId),
      }
    })
    deleteNotebookEntry(noteId).catch(err => {
      console.error('Failed to delete note:', err)
      setData(prev => {
        if (!prev) return prev
        return { ...prev, notes: [...prev.notes, ...removedNotes] }
      })
    })
  }, [data, currentPageId, navigateBack])

  const createAndNavigate = useCallback((parentId: string | null) => {
    if (!data) return
    const tempId = `note-${Date.now()}`
    const newNote: NotebookNote = {
      id: tempId,
      parentId,
      sortOrder: 0,
      scope: 'core',
      entryDate: null,
      title: '',
      summary: '',
      icon: '📄',
      contentMd: '',
      confidence: null,
      tags: [],
      pinned: false,
      breadcrumb: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setData(prev => {
      if (!prev) return prev
      const notes = [newNote, ...prev.notes].map(n => {
        if (n.id === parentId) {
          return { ...n, childCount: (n.childCount ?? 0) + 1 }
        }
        return n
      })
      return { ...prev, notes }
    })
    setNewPageId(tempId)
    navigateToPage(tempId)
    // Auto-expand the parent node so the new child is visible in the sidebar
    if (parentId) {
      setAutoExpandIds(prev => prev.includes(parentId) ? prev : [...prev, parentId])
    }
    createNotebookEntry({
      title: '',
      contentMd: '',
      tags: [],
      icon: '📄',
      parentId,
    }).then(serverNote => {
      // Register stable key BEFORE state updates so the next render uses it.
      // This prevents AnimatePresence from unmounting/remounting the page view
      // (which causes a blank screen on iOS when the Tiptap editor reinitializes).
      stableKeyMap.current.set(serverNote.id, tempId)

      // Merge locally-edited fields from the temp note into the server note
      // so that any title/content the user typed before the server responded is preserved
      setData(prev => {
        if (!prev) return prev
        const tempNote = prev.notes.find(n => n.id === tempId)
        const merged = tempNote
          ? {
              ...serverNote,
              title: tempNote.title || serverNote.title,
              summary: tempNote.summary || serverNote.summary,
              contentMd: tempNote.contentMd || serverNote.contentMd,
              icon: tempNote.icon !== '📄' ? tempNote.icon : serverNote.icon,
            }
          : serverNote
        return {
          ...prev,
          notes: prev.notes.map(n => n.id === tempId ? merged : n),
        }
      })
      setCurrentPageId(prev => prev === tempId ? serverNote.id : prev)
      setNavStack(prev => prev.map(id => id === tempId ? serverNote.id : id))
      setNewPageId(prev => prev === tempId ? serverNote.id : prev)
    }).catch(err => {
      console.error('Failed to create notebook entry:', err)
      // Remove the temp note and navigate back so the user isn't stuck on a dead page
      setData(prev => {
        if (!prev) return prev
        return { ...prev, notes: prev.notes.filter(n => n.id !== tempId) }
      })
      setCurrentPageId(prev => prev === tempId ? null : prev)
      setNavStack(prev => prev.filter(id => id !== tempId))
      setNewPageId(prev => prev === tempId ? null : prev)
    })
  }, [data, navigateToPage])

  // Ref to read current data in callbacks without adding it as a dependency
  const dataRef = useRef(data)
  dataRef.current = data

  // Serialize saves per note: prevent concurrent PATCHes that cause stale-write races
  const inFlightSaves = useRef(new Set<string>())
  const pendingSaves = useRef(new Map<string, Partial<NotebookNote>>())

  const doSave = useCallback((noteId: string, updates: Partial<NotebookNote>) => {
    inFlightSaves.current.add(noteId)
    const note = dataRef.current?.notes.find(n => n.id === noteId)
    updateNotebookEntry(noteId, { ...updates, updatedAt: note?.updatedAt })
      .then(serverNote => {
        // Update updatedAt from server response so next save uses the correct version
        setData(prev => {
          if (!prev) return prev
          return {
            ...prev,
            notes: prev.notes.map(n =>
              n.id === noteId ? { ...n, updatedAt: serverNote.updatedAt } : n
            ),
          }
        })
      })
      .catch(err => {
        if (err instanceof ApiError && err.status === 409 && err.body?.entry) {
          // Conflict: server has a newer version — accept it
          const serverEntry = err.body.entry as NotebookNote
          setData(prev => {
            if (!prev) return prev
            return {
              ...prev,
              notes: prev.notes.map(n =>
                n.id === noteId ? { ...n, ...serverEntry } : n
              ),
            }
          })
        } else {
          console.error('Failed to update note:', err)
        }
      })
      .finally(() => {
        inFlightSaves.current.delete(noteId)
        // Process queued update with the now-current updatedAt
        const pending = pendingSaves.current.get(noteId)
        if (pending) {
          pendingSaves.current.delete(noteId)
          doSave(noteId, pending)
        }
      })
  }, [])

  const handleUpdateNote = useCallback((noteId: string, updates: Partial<NotebookNote>) => {
    // Optimistic local update (don't override updatedAt — keep server's value for concurrency)
    setData(prev => {
      if (!prev) return prev
      return {
        ...prev,
        notes: prev.notes.map(n =>
          n.id === noteId ? { ...n, ...updates } : n
        ),
      }
    })
    if (noteId.startsWith('note-')) return

    if (inFlightSaves.current.has(noteId)) {
      // Queue: merge with any pending updates, will send after in-flight completes
      const existing = pendingSaves.current.get(noteId) ?? {}
      pendingSaves.current.set(noteId, { ...existing, ...updates })
      return
    }
    doSave(noteId, updates)
  }, [doSave])

  const handleCreateChild = useCallback((parentId: string | null) => {
    createAndNavigate(parentId)
  }, [createAndNavigate])

  const handleMoveNote = useCallback((noteId: string, newParentId: string | null, newSortOrder: number) => {
    if (!data) return

    const prevNotes = data.notes

    setData(prev => {
      if (!prev) return prev
      const notes = prev.notes.map(n =>
        n.id === noteId ? { ...n, parentId: newParentId, sortOrder: newSortOrder } : n
      )
      return { ...prev, notes }
    })

    if (noteId.startsWith('note-')) return

    const updatedNotes = data.notes.map(n =>
      n.id === noteId ? { ...n, parentId: newParentId, sortOrder: newSortOrder } : n
    )
    const siblings = updatedNotes
      .filter(n => n.parentId === newParentId)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    let needsReindexing = false
    for (let i = 0; i < siblings.length - 1; i++) {
      const current = siblings[i]
      const next = siblings[i + 1]
      if (current && next && needsReindex(current.sortOrder, next.sortOrder)) {
        needsReindexing = true
        break
      }
    }

    if (needsReindexing) {
      const reindexed = reindexSiblings(updatedNotes, newParentId)
      setData(prev => {
        if (!prev) return prev
        const reindexMap = new Map(reindexed.map(r => [r.id, r.sortOrder]))
        const notes = prev.notes.map(n => {
          const newOrder = reindexMap.get(n.id)
          return newOrder !== undefined ? { ...n, sortOrder: newOrder } : n
        })
        return { ...prev, notes }
      })
      batchUpdateSortOrder(reindexed.map(r => ({ id: r.id, sortOrder: r.sortOrder, parentId: newParentId }))).catch(err => {
        console.error('Failed to reindex notes:', err)
        setData(prev => prev ? { ...prev, notes: prevNotes } : prev)
      })
    } else {
      updateNotebookEntry(noteId, { parentId: newParentId, sortOrder: newSortOrder }).catch(err => {
        console.error('Failed to move note:', err)
        setData(prev => prev ? { ...prev, notes: prevNotes } : prev)
      })
    }
  }, [data])

  const handleNewPage = useCallback(() => {
    createAndNavigate(null)
  }, [createAndNavigate])

  // Wait for any pending or in-flight save on this note to settle. Used by the
  // meeting-summary regenerate flow so the backend reads the latest user notes
  // from the DB instead of a stale snapshot from before the user typed.
  const flushSaves = useCallback(async (noteId: string): Promise<void> => {
    const start = Date.now()
    const TIMEOUT_MS = 5_000
    while (
      (inFlightSaves.current.has(noteId) || pendingSaves.current.has(noteId)) &&
      Date.now() - start < TIMEOUT_MS
    ) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
  }, [])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pendingPollRef.current) {
        clearInterval(pendingPollRef.current)
        pendingPollRef.current = null
      }
    }
  }, [])

  return {
    data,
    setData,
    loading,
    currentPageId,
    navStack,
    newPageId,
    autoExpandIds,
    getStableKey,
    navigateToPage,
    navigateBack,
    handleTogglePin,
    handleDeleteNote,
    handleUpdateNote,
    handleCreateChild,
    handleMoveNote,
    handleNewPage,
    loadData,
    flushSaves,
  }
}
